import {
    Component,
    DestroyRef,
    Input,
    inject,
    ViewChildren,
    QueryList,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, FormRecord, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CustomFieldOptionModel } from "@app/incident/models/custom-field-option.model";
import { IncidentTypeDisplayModel } from "@app/incident/models/incident-type-display.model";
import { IncidentCustomFieldService } from "@app/incident/services/incident-custom-field.service";
import { FieldType } from "@app/preset/models/field-type.enum";
import { Observable, forkJoin } from "rxjs";
import { IncidentCustomFieldsSectionComponent } from "./incident-custom-fields-section/incident-custom-fields-section.component";
import { TreeItemNode } from "@app/incident/components/src/lib/core/models/tree/tree-item-node.model";
import { SelectOptionModel } from "@app/incident/components/src/lib/core/models/select-option.model";
import { CurrencyInterface } from "@app/incident/components/src/lib/core/models/interfaces/currency.model";

@Component({
    selector: "incident-custom-fields-tab",
    templateUrl: "./incident-custom-fields-tab.component.html",
    styleUrls: ["./incident-custom-fields-tab.component.scss"],
    imports: [FormsModule, ReactiveFormsModule, IncidentCustomFieldsSectionComponent],
    providers: [],
})
export class IncidentCustomFieldsTabcontentComponent {
    @Input() public customFieldsForm: FormRecord<FormGroup>;
    @Input() public incidentType: IncidentTypeDisplayModel;

    private _currentSelectedOrganizations: TreeItemNode[] = [];
    @Input()
    get currentSelectedOrganizations(): TreeItemNode[] {
        return this._currentSelectedOrganizations;
    }
    set currentSelectedOrganizations(organizations: TreeItemNode[]) {
        this._currentSelectedOrganizations = organizations;
    }

    private _currentSelectedThirdParties: SelectOptionModel[] = [];
    @Input()
    get currentSelectedThirdParties(): SelectOptionModel[] {
        return this._currentSelectedThirdParties;
    }
    set currentSelectedThirdParties(thirdParties: SelectOptionModel[]) {
        this._currentSelectedThirdParties = thirdParties;
    }

    private _currencies: CurrencyInterface[];
    @Input()
    get currencies(): CurrencyInterface[] {
        return this._currencies;
    }
    set currencies(currencies: CurrencyInterface[]) {
        this._currencies = currencies;
    }

    @Input() public dataUiid: string = "undefined";
    @ViewChildren(IncidentCustomFieldsSectionComponent)
    public customFieldSections: QueryList<IncidentCustomFieldsSectionComponent>;

    private _isActive: boolean = false;
    @Input()
    get isActive(): boolean {
        return this._isActive;
    }
    set isActive(newIsActive: boolean) {
        this._isActive = newIsActive;

        this.loadCustomFieldOptions();
        if (this.incidentType) {
            for (const its of this.incidentType.incidentTypeSections) {
                if (its.customFields && its.customFields.length > 0) {
                    this.flatSection = false;
                    break;
                }
            }
        }
    }

    private _refresh: boolean = false;
    @Input()
    get refresh(): boolean {
        return this._refresh;
    }
    set refresh(newRefresh: boolean) {
        this._refresh = newRefresh;
        if (this._refresh) {
            this.cfOptionsLoaded = false;

            this.loadCustomFieldOptions();
        }
    }

    private destroyRef = inject(DestroyRef);
    private customFieldService = inject(IncidentCustomFieldService);

    private loadingOptions: boolean = false;
    private cfOptionsLoaded: boolean = false;
    protected customFieldOptions = new Map<string, CustomFieldOptionModel[]>();

    protected readonly FieldType: typeof FieldType = FieldType;
    protected readonly ObjectKeys = Object.keys;
    protected readonly fullWidthCustomTypes: FieldType[] = [
        FieldType.TextBox,
        FieldType.FinancialImpact,
    ];

    protected flatSection: boolean = true;

    private loadCustomFieldOptions() {
        if (!this.cfOptionsLoaded && this._isActive && !this.loadingOptions && this.incidentType) {
            const selectCustomFieldIds = this.getCustomFieldOfTypeSelect();

            if (Object.keys(selectCustomFieldIds).length) {
                const requests: {
                    groupId: string;
                    id: string;
                    request: Observable<CustomFieldOptionModel[]>;
                }[] = [];

                Object.entries(selectCustomFieldIds).forEach(([groupId, value]) => {
                    value.forEach((id) => {
                        const formGroup = this.customFieldsForm.controls[groupId] as FormRecord;
                        if (formGroup && formGroup.controls[id]) {
                            const selectedOptions =
                                typeof formGroup.controls[id].value === "string"
                                    ? [formGroup.controls[id].value]
                                    : formGroup.controls[id].value;
                            requests.push({
                                groupId,
                                id,
                                request: this.customFieldService
                                    .getVisibleCustomFieldOptions(id, selectedOptions)
                                    .pipe(takeUntilDestroyed(this.destroyRef)),
                            });
                        }
                    });
                });

                if (requests.length) {
                    this.loadingOptions = true;

                    forkJoin(requests.map((req) => req.request))
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (allOptions) => {
                                allOptions.forEach((options, index) => {
                                    if (this.incidentType.id === requests[index].groupId) {
                                        this.incidentType.customFields.forEach((cf) => {
                                            if (cf.id === requests[index].id) {
                                                cf.options = options;
                                            }
                                        });
                                    } else {
                                        this.incidentType.incidentTypeSections
                                            .find((its) => its.id === requests[index].groupId)
                                            .customFields.forEach((cf) => {
                                                if (cf.id === requests[index].id) {
                                                    cf.options = options;
                                                }
                                            });
                                    }
                                });

                                this.cfOptionsLoaded = true;
                                this.loadingOptions = false;
                            },
                            error: () => {
                                this.loadingOptions = false;
                            },
                        });
                }
            }
        }
    }

    private getCustomFieldOfTypeSelect(): object {
        const selectCustomFieldIds: object = {};
        selectCustomFieldIds[this.incidentType.id] = [];

        if (this.incidentType) {
            this.incidentType.customFields?.forEach((cf) => {
                if (
                    cf.customFieldType === FieldType.SingleSelect ||
                    cf.customFieldType === FieldType.MultiSelect
                ) {
                    selectCustomFieldIds[this.incidentType.id].push(cf.id);
                }
            });
            this.incidentType.incidentTypeSections.forEach((its) => {
                selectCustomFieldIds[its.id] = [];
                its.customFields?.forEach((cf) => {
                    if (
                        cf.customFieldType === FieldType.SingleSelect ||
                        cf.customFieldType === FieldType.MultiSelect
                    ) {
                        selectCustomFieldIds[its.id].push(cf.id);
                    }
                });
            });
        }

        return selectCustomFieldIds;
    }
}
